import { usePathname, useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { useToaster } from "~/modules/toaster/hooks/useToaster";
import * as UserAnalytics from "~/modules/user/analytics";
import { useUser } from "~/modules/user/hooks/useUser";
import { useSyncUserSettings } from "~/modules/user/hooks/useSyncUserSettings";
import { EMPTY_USER_STATE } from "~/modules/user/store/consts";
import {
    UserProfile,
    UserProfileAcquisitionChannels,
    UserProfileCompanyTypes,
    UserProfileOccupations,
} from "~/modules/user/store/types";
import { Routes } from "~/routes";
import { api } from "~/utils/api";
import * as Analytics from "../../analytics";
import {
    EMPTY_INLINE_ONBOARDING_FORM_VALUES,
    InlineOnboardingFormValues,
    InlineOnboardingModalLayout,
} from "./InlineOnboardingModalLayout";

// // // //

interface Props {
    onComplete?: () => void;
}

/**
 * InlineOnboardingModal
 * Handles user onboarding after authentication is complete
 * Self-manages visibility based on user onboarding status and feature flags
 */
export function InlineOnboardingModal({ onComplete }: Props) {
    const router = useRouter();
    const pathname = usePathname();
    const { user } = useUser();
    const { addToaster } = useToaster();
    const { syncProfile } = useSyncUserSettings();

    const [show, setShow] = useState(false);
    const [redirectPath, setRedirectPath] = useState("");

    // Onboarding form state
    const [values, setValues] = useState<InlineOnboardingFormValues>(
        EMPTY_INLINE_ONBOARDING_FORM_VALUES,
    );
    const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

    const updateReq = api.userSettings.updateProfile.useMutation();
    const updateNameReq = api.users.updateName.useMutation();

    const loading = updateReq.isLoading || updateNameReq.isLoading;

    // Check if user is authenticated
    const isLoggedIn = user !== undefined && user.id !== EMPTY_USER_STATE.id;

    // Only show onboarding for users created after 2025-01-13
    const ONBOARDING_CUTOFF_DATE = new Date("2025-01-13");
    const isNewUser =
        user?.createdAt && new Date(user.createdAt) >= ONBOARDING_CUTOFF_DATE;

    // Check if user needs onboarding
    const needsOnboarding =
        isLoggedIn &&
        user &&
        isNewUser &&
        (user.profile.acquisitionChannel ===
            UserProfileAcquisitionChannels.None ||
            user.profile.companyType === UserProfileCompanyTypes.None ||
            user.profile.company.trim() === "");

    // Update form state when user changes
    useEffect(() => {
        if (user) {
            setValues((prev) => ({ ...prev, name: user.name || "" }));
        }
    }, [user]);

    // Determine if modal should show
    useEffect(() => {
        if (!isLoggedIn || !needsOnboarding) {
            setShow(false);
            return;
        }

        Analytics.trackInlineOnboardingModalOpen();
        // NOTE - read from the address bar to keep the query string + hash, which
        // `usePathname` drops. `pathname` re-runs this effect on navigation.
        setRedirectPath(
            `${window.location.pathname}${window.location.search}${window.location.hash}`,
        );
        setShow(true);
    }, [isLoggedIn, needsOnboarding, pathname]);

    const onClose = () => {
        setShow(false);
    };

    // Onboarding validation
    const isGoogleUser = Boolean(user?.name);
    const isOnboardingFormValid =
        user &&
        (isGoogleUser || values.name.trim() !== "") &&
        values.company.trim() !== "" &&
        values.companyType !== UserProfileCompanyTypes.None &&
        values.acquisitionChannel !== UserProfileAcquisitionChannels.None;

    const errors = {
        name: hasAttemptedSubmit && !isGoogleUser && values.name.trim() === "",
        company: hasAttemptedSubmit && values.company.trim() === "",
        companyType:
            hasAttemptedSubmit &&
            values.companyType === UserProfileCompanyTypes.None,
        acquisitionChannel:
            hasAttemptedSubmit &&
            values.acquisitionChannel === UserProfileAcquisitionChannels.None,
    };

    const handleOnboardingSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setHasAttemptedSubmit(true);

        if (!isOnboardingFormValid || loading || !user) return;

        try {
            if (!isGoogleUser && values.name.trim() !== "") {
                await updateNameReq.mutateAsync({ name: values.name.trim() });
            }

            const profileData: UserProfile = {
                company: values.company.trim(),
                companyType: values.companyType,
                acquisitionChannel: values.acquisitionChannel,
                assetClassInterests: [], // Default to empty array since we removed the field
                shouldReceiveMarketing: values.shouldReceiveMarketing,
                occupation: UserProfileOccupations.Other,
            };

            await updateReq.mutateAsync({
                profile: {
                    ...user.profile,
                    ...profileData,
                },
            });

            UserAnalytics.trackNewUserProfileSubmitSuccess();
            addToaster({
                variant: "success",
                message: "Welcome to RWA.xyz! Your profile has been created.",
            });

            syncProfile(profileData);
            onClose();

            // Call completion callback if provided
            if (onComplete) {
                onComplete();
            }

            // Ensure we're redirecting to the original page, not home
            if (
                redirectPath &&
                redirectPath !== "/" &&
                redirectPath !== Routes.home
            ) {
                router.push(redirectPath);
            } else {
                // Don't redirect if the path is home - just close modal
            }
        } catch (error) {
            UserAnalytics.trackNewUserProfileSubmitError();
            addToaster({
                variant: "danger",
                message: "Error creating your profile - please try again.",
            });
        }
    };

    return (
        <InlineOnboardingModalLayout
            show={show}
            values={values}
            errors={errors}
            showNameField={!isGoogleUser}
            loading={loading}
            onChange={setValues}
            onSubmit={handleOnboardingSubmit}
        />
    );
}
